export interface JurisdictionPreset {
  id: string;
  name: string;
  flag: string;
  currencySymbol: string;
  currencyCode: string;
  vatRate: number;
  taxAuthorityLabel: string;
  taxNumberLabel: string;
  invoiceTypeLabel: string;
  fiscalYearEndMonth: number;
  popularBanks: string;
  description: string;
  // Build-Phase & Statutory Thresholds
  vatThresholdAmount: number;
  vatThresholdFormatted: string;
  taxFreeAllowanceAmount: number;
  taxFreeAllowanceFormatted: string;
  nilReturnTitle: string;
  nilReturnAuthorityForm: string;
  thresholdMilestoneLabel: string;
  buildPhaseAdvice: string;
}

export const JURISDICTION_PRESETS: Record<string, JurisdictionPreset> = {
  ZA: {
    id: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦',
    currencySymbol: 'R',
    currencyCode: 'ZAR',
    vatRate: 15,
    taxAuthorityLabel: 'SARS (South African Revenue Service)',
    taxNumberLabel: 'VAT Reg / Income Tax #',
    invoiceTypeLabel: 'Tax Invoice',
    fiscalYearEndMonth: 2, // February
    popularBanks: 'FNB, Standard Bank, Nedbank, Capitec Business, Absa',
    description: '15% Standard VAT, February fiscal year-end, SARS compliance terminology',
    vatThresholdAmount: 1000000,
    vatThresholdFormatted: 'R1,000,000 / yr (Compulsory VAT Registration)',
    taxFreeAllowanceAmount: 95750,
    taxFreeAllowanceFormatted: 'R95,750 0% SBC / Personal Income Bracket',
    nilReturnTitle: 'SARS IRP6 Zero-Liability (Nil Return) Pack',
    nilReturnAuthorityForm: 'SARS eFiling IRP6 Provisional Return & IT14/ITR12',
    thresholdMilestoneLabel: 'Road to R1,000,000 SARS VAT Threshold',
    buildPhaseAdvice: 'Under R1M turnover you do not collect or remit 15% VAT. Under ~R95,750 taxable profit (or if expenses exceed income), your income tax liability is R0. If registered for Provisional Tax, submit a Nil (R0) IRP6 to prevent admin penalties.'
  },
  US: {
    id: 'US',
    name: 'United States',
    flag: '🇺🇸',
    currencySymbol: '$',
    currencyCode: 'USD',
    vatRate: 0,
    taxAuthorityLabel: 'IRS / State Dept of Revenue',
    taxNumberLabel: 'EIN / Federal Tax ID (SSN)',
    invoiceTypeLabel: 'Commercial Invoice',
    fiscalYearEndMonth: 12, // December
    popularBanks: 'Chase, Mercury, Relay, Silicon Valley Bank, Brex, BoA',
    description: '0% Base / Sales Tax buffer, December calendar year-end, 1099/EIN terminology',
    vatThresholdAmount: 100000,
    vatThresholdFormatted: '$100,000 / yr (Typical State Economic Nexus)',
    taxFreeAllowanceAmount: 14600,
    taxFreeAllowanceFormatted: '$14,600 Standard Deduction ($29,200 Joint)',
    nilReturnTitle: 'IRS Schedule C Zero-Liability & Nil Filing Pack',
    nilReturnAuthorityForm: 'IRS Form 1040 Schedule C / 1065 / 1120-S Nil Return',
    thresholdMilestoneLabel: 'Road to $100,000 Economic Nexus Milestone',
    buildPhaseAdvice: 'Net earnings under $400 have $0 self-employment tax. If startup expenses exceed revenue, claim a net operating loss (NOL) to carry forward and shelter future revenue. File Schedule C or 1120 with $0 tax liability.'
  },
  GB: {
    id: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    currencySymbol: '£',
    currencyCode: 'GBP',
    vatRate: 20,
    taxAuthorityLabel: 'HMRC (HM Revenue & Customs)',
    taxNumberLabel: 'VAT Registration Number (VRN) / UTR',
    invoiceTypeLabel: 'VAT Invoice',
    fiscalYearEndMonth: 3, // March / April 5
    popularBanks: 'Starling Business, Monzo, Barclays, NatWest, Lloyds, Tide',
    description: '20% Standard VAT, March/April fiscal year-end, Making Tax Digital (MTD)',
    vatThresholdAmount: 90000,
    vatThresholdFormatted: '£90,000 / yr (Compulsory VAT Threshold)',
    taxFreeAllowanceAmount: 12570,
    taxFreeAllowanceFormatted: '£12,570 Personal Allowance (£1,000 Trading Allowance)',
    nilReturnTitle: 'HMRC Self-Assessment Nil Return & Loss Schedule',
    nilReturnAuthorityForm: 'HMRC SA100 / SA103 / CT600 Dormant or Nil Return',
    thresholdMilestoneLabel: 'Road to £90,000 HMRC VAT Registration',
    buildPhaseAdvice: 'Gross trading income under £1,000 is 100% tax-free with zero reporting required. Below £90,000 turnover, no VAT registration is mandatory. Pre-trading startup expenses can be carried back up to 7 years.'
  },
  IN: {
    id: 'IN',
    name: 'India',
    flag: '🇮🇳',
    currencySymbol: '₹',
    currencyCode: 'INR',
    vatRate: 18,
    taxAuthorityLabel: 'GST Council / Income Tax Dept',
    taxNumberLabel: 'GSTIN / PAN Number',
    invoiceTypeLabel: 'GST Tax Invoice',
    fiscalYearEndMonth: 3, // March 31
    popularBanks: 'HDFC Bank, ICICI Bank, RazorpayX, Axis Bank, SBI, Kotak',
    description: '18% Standard Services GST, March 31 fiscal year-end, GSTIN & HSN/SAC ready',
    vatThresholdAmount: 2000000,
    vatThresholdFormatted: '₹20 Lakhs / yr (GST Exemption Threshold for Services)',
    taxFreeAllowanceAmount: 700000,
    taxFreeAllowanceFormatted: '₹7,00,000 Rebate under Section 87A (New Regime)',
    nilReturnTitle: 'ITR Nil Return & GSTR-1/3B Nil Filing Pack',
    nilReturnAuthorityForm: 'Income Tax ITR-1/ITR-3 Nil & GST CMP-08 / GSTR-3B Nil',
    thresholdMilestoneLabel: 'Road to ₹20,00,000 GST Registration Threshold',
    buildPhaseAdvice: 'Service providers with turnover under ₹20 Lakhs (goods under ₹40 Lakhs) are completely exempt from GST registration. Income under ₹7 Lakhs has ₹0 net tax liability under Section 87A rebate.'
  },
  AU: {
    id: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    currencySymbol: 'A$',
    currencyCode: 'AUD',
    vatRate: 10,
    taxAuthorityLabel: 'ATO (Australian Taxation Office)',
    taxNumberLabel: 'ABN (Australian Business Number)',
    invoiceTypeLabel: 'Tax Invoice',
    fiscalYearEndMonth: 6, // June 30
    popularBanks: 'Commonwealth Bank, ANZ, NAB, Westpac, Judo Bank, Macquarie',
    description: '10% Standard GST, June 30 fiscal year-end, BAS & ABN terminology',
    vatThresholdAmount: 75000,
    vatThresholdFormatted: 'A$75,000 / yr (Compulsory GST Registration)',
    taxFreeAllowanceAmount: 18200,
    taxFreeAllowanceFormatted: 'A$18,200 Tax-Free Threshold',
    nilReturnTitle: 'ATO Nil Activity Statement & Non-Lodgment Advice',
    nilReturnAuthorityForm: 'ATO Nil BAS / Non-Lodgment Advice (NLA)',
    thresholdMilestoneLabel: 'Road to A$75,000 ATO GST Threshold',
    buildPhaseAdvice: 'Turnover under A$75,000 requires no GST registration. First A$18,200 is 0% taxed. If your sole trader business operated at a loss, declare a Nil BAS or Non-Lodgment advice while preserving deductible business expenses.'
  },
  EU: {
    id: 'EU',
    name: 'European Union (General)',
    flag: '🇪🇺',
    currencySymbol: '€',
    currencyCode: 'EUR',
    vatRate: 21,
    taxAuthorityLabel: 'National Tax Administration (EU VAT)',
    taxNumberLabel: 'EU VAT Identification Number',
    invoiceTypeLabel: 'VAT Invoice',
    fiscalYearEndMonth: 12, // December
    popularBanks: 'Revolut Business, N26, BNP Paribas, Deutsche Bank, ING',
    description: 'Standard EU VAT structure, December calendar year-end, reverse charge ready',
    vatThresholdAmount: 25000,
    vatThresholdFormatted: '€25,000 / yr (EU SME Small Business Exemption Scheme)',
    taxFreeAllowanceAmount: 11000,
    taxFreeAllowanceFormatted: '€11,000 Approximate Base Allowance',
    nilReturnTitle: 'EU Zero-VAT Recapitulative & Nil Declaration Pack',
    nilReturnAuthorityForm: 'EU National Nil VAT Return & Annual Corporate Nil Statement',
    thresholdMilestoneLabel: 'Road to €25,000 EU SME Exemption Threshold',
    buildPhaseAdvice: 'Most EU member states offer small business / micro-enterprise exemptions (e.g., Kleinunternehmer in Germany up to €25,000, Micro-Entreprise in France). Under threshold, you invoice without VAT and submit simplified nil declarations.'
  }
};

export const JURISDICTION_LIST: JurisdictionPreset[] = Object.values(JURISDICTION_PRESETS);

export function getJurisdictionPreset(id?: string): JurisdictionPreset {
  if (!id) return JURISDICTION_PRESETS.ZA;
  return JURISDICTION_PRESETS[id.toUpperCase()] || JURISDICTION_PRESETS.ZA;
}
